import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Upload, CheckCircle, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Business Type Groups for Display
const BUSINESS_TYPE_GROUPS = {
  'Travel Trade / Buyers & Distributors': [
    { value: 'travel_agency', label: 'Travel Agency' },
    { value: 'travel_advisor', label: 'Travel Advisor' },
    { value: 'wholesaler', label: 'Wholesaler' },
    { value: 'consortium_network', label: 'Consortium / Travel Network' },
    { value: 'ota_platform', label: 'OTA Platform' },
    { value: 'destination_specialist', label: 'Destination Specialist' }
  ],
  'Travel Product & Service Suppliers': [
    { value: 'tour_operator', label: 'Tour Operator' },
    { value: 'dmc', label: 'DMC' },
    { value: 'scheduled_tour_operator', label: 'Scheduled Tour Operator' },
    { value: 'package_tour_supplier', label: 'Package Tour Supplier' },
    { value: 'hotel_resort', label: 'Hotel / Resort' },
    { value: 'villa_accommodation', label: 'Villa / Accommodation Provider' },
    { value: 'attraction_experience', label: 'Attraction / Experience Provider' },
    { value: 'bus_coach_operator', label: 'Bus / Coach Operator' },
    { value: 'car_rental', label: 'Car Rental' },
    { value: 'transfer_transportation', label: 'Transfer / Transportation Provider' },
    { value: 'rail_cruise_transport', label: 'Rail / Cruise / Other Transport' },
    { value: 'airport_services', label: 'Airport Services' },
    { value: 'travel_insurance', label: 'Travel Insurance Provider' },
    { value: 'visa_immigration', label: 'Visa / Immigration Services' },
    { value: 'other_travel_service', label: 'Other Travel Service' }
  ],
  'Destination Partners': [
    { value: 'tourism_board', label: 'Tourism Board / Tourism Authority' },
    { value: 'dmo', label: 'Destination Marketing Organization (DMO)' },
    { value: 'cvb', label: 'Convention & Visitors Bureau (CVB)' },
    { value: 'city_regional_tourism', label: 'City / Regional Tourism Organization' },
    { value: 'national_tourism', label: 'National Tourism Organization' },
    { value: 'tourism_association', label: 'Tourism Association' },
    { value: 'destination_promotion', label: 'Destination Promotion Organization' },
    { value: 'destination_organization', label: 'Other Destination Organization' }
  ],
  'Other': [
    { value: 'other', label: 'Other' }
  ]
};

const TARGET_MARKETS = [
  { value: 'indonesia', label: 'Indonesia' },
  { value: 'international', label: 'International' },
  { value: 'southeast_asia', label: 'Southeast Asia' },
  { value: 'europe', label: 'Europe' },
  { value: 'usa_canada', label: 'USA / Canada' },
  { value: 'australia_new_zealand', label: 'Australia / New Zealand' },
  { value: 'middle_east', label: 'Middle East' },
  { value: 'asia', label: 'Asia' },
  { value: 'other', label: 'Other' }
];

const SERVICES_OFFERED = [
  { value: 'tours', label: 'Tours' },
  { value: 'scheduled_tours', label: 'Scheduled Tours' },
  { value: 'private_tours', label: 'Private Tours' },
  { value: 'package_tours', label: 'Package Tours' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'airport_services', label: 'Airport Services' },
  { value: 'car_rental', label: 'Car Rental' },
  { value: 'travel_insurance', label: 'Travel Insurance' },
  { value: 'visa_immigration', label: 'Visa / Immigration Services' },
  { value: 'attractions', label: 'Attractions' },
  { value: 'activities', label: 'Activities' },
  { value: 'experiences', label: 'Experiences' },
  { value: 'mice', label: 'MICE' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'other', label: 'Other' }
];

const DESTINATION_CAPABILITIES = [
  { value: 'destination_promotion', label: 'Destination Promotion' },
  { value: 'trade_support', label: 'Trade Support' },
  { value: 'famtrip_hosting', label: 'FAM Trip Hosting' },
  { value: 'media_press_trip', label: 'Media / Press Trips' },
  { value: 'trade_events', label: 'Trade Events & Roadshows' },
  { value: 'destination_training', label: 'Destination Training' },
  { value: 'b2b_networking', label: 'B2B Networking' },
  { value: 'member_support', label: 'Member Support' },
  { value: 'other', label: 'Other' }
];

export const B2BRegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Core fields
    company_name: '',
    country: '',
    city: '',
    contact_person: '',
    designation: '',
    business_email: '',
    phone: '',
    whatsapp: '',
    website: '',
    company_address: '',
    postal_code: '',
    organization_description: '',
    
    // New fields
    partner_role: [],
    business_type: '',
    target_markets: [],
    distribution_channels: [],
    services_offered: [],
    destination_coverage: '',
    destination_capabilities: [],
    represented_destinations: '',
    is_government_entity: false,
    
    // Regulatory
    license_type: '',
    license_number: '',
    issuing_authority: '',
    
    // Deprecated (for backward compat)
    license_number_old: ''
  });
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleMultiSelect = (name, value) => {
    const currentValues = formData[name] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setFormData({ ...formData, [name]: newValues });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    for (const file of files) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please upload PDF, JPG, or PNG only.');
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB per file.');
        continue;
      }
      validFiles.push(file);
    }
    
    setDocuments([...documents, ...validFiles]);
    setError('');
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (documents.length === 0) {
      setError('Please upload at least one business document.');
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add documents
      documents.forEach((doc, index) => {
        formDataToSend.append(`document_${index}`, doc);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/register`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/b2b/login');
        }, 5000);
      } else {
        setError(data.detail || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isSupplierOrDistributor = formData.partner_role.includes('supplier') || formData.partner_role.includes('distributor');
  const isDestinationPartner = formData.partner_role.includes('destination_partner');

  if (success) {
    return (
      <div style={{ background: '#0B1120', minHeight: '100vh' }}>
        <Navigation />
        <div className="container" style={{ padding: '120px 20px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            background: 'rgba(40, 167, 69, 0.1)',
            border: '2px solid #28a745',
            borderRadius: '8px',
            padding: '40px',
            marginBottom: '40px'
          }}>
            <CheckCircle size={64} style={{ color: '#28a745', marginBottom: '20px' }} />
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px', color: '#28a745' }}>
              Registration Successful!
            </h2>
            <p style={{ fontSize: '16px', color: '#F7F3E8', marginBottom: '8px' }}>
              Thank you for your application. We have received your B2B partner registration.
            </p>
            <p style={{ fontSize: '14px', color: 'rgba(247, 243, 232, 0.8)' }}>
              Our team will review your application and contact you shortly.
            </p>
            <p style={{ fontSize: '14px', color: 'rgba(247, 243, 232, 0.8)', marginTop: '20px' }}>
              Redirecting to login page...
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '12px',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    borderRadius: '4px',
    fontSize: '14px',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#F7F3E8',
    transition: 'border-color 0.2s ease'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#F7F3E8'
  };

  const checkboxStyle = {
    marginRight: '8px',
    transform: 'scale(1.2)'
  };

  return (
    <div style={{ background: '#0B1120', minHeight: '100vh' }}>
      <Navigation />
      <div className="container" style={{ padding: '120px 20px 60px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '16px', color: '#F7F3E8' }}>
            B2B Partner Registration
          </h1>
          <p style={{ fontSize: '16px', color: 'rgba(247, 243, 232, 0.8)' }}>
            Join our global network of travel trade partners
          </p>
          
          {/* Progress indicator */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px', gap: '10px' }}>
            {[1, 2, 3, 4].map(s => (
              <div key={s} style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: step >= s ? '#D4AF37' : 'rgba(255, 255, 255, 0.1)',
                border: step >= s ? '2px solid #D4AF37' : '2px solid rgba(255, 255, 255, 0.2)',
                color: step >= s ? '#0B1120' : 'rgba(247, 243, 232, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}>
                {s}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(220, 53, 69, 0.1)',
            border: '2px solid #dc3545',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={24} style={{ color: '#dc3545' }} />
            <p style={{ margin: 0, color: '#dc3545' }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* STEP 1: Partnership Role */}
          {step === 1 && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '40px', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#F7F3E8' }}>
                Step 1: How would you like to work with Indotravelista?
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '24px' }}>
                Select all that apply. You can choose multiple roles.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'start', padding: '16px', border: '2px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', cursor: 'pointer', background: formData.partner_role.includes('supplier') ? 'rgba(212, 175, 55, 0.1)' : 'rgba(255, 255, 255, 0.02)' }}>
                  <input
                    type="checkbox"
                    checked={formData.partner_role.includes('supplier')}
                    onChange={() => handleMultiSelect('partner_role', 'supplier')}
                    style={checkboxStyle}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#F7F3E8' }}>Supplier / Product Partner</div>
                    <div style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)' }}>You sell travel products or services (tours, accommodation, transportation, etc.)</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'start', padding: '16px', border: '2px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', cursor: 'pointer', background: formData.partner_role.includes('distributor') ? 'rgba(212, 175, 55, 0.1)' : 'rgba(255, 255, 255, 0.02)' }}>
                  <input
                    type="checkbox"
                    checked={formData.partner_role.includes('distributor')}
                    onChange={() => handleMultiSelect('partner_role', 'distributor')}
                    style={checkboxStyle}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#F7F3E8' }}>Distribution / Travel Trade Partner</div>
                    <div style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)' }}>You distribute or wholesale travel products (travel agency, wholesaler, consortium)</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'start', padding: '16px', border: '2px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', cursor: 'pointer', background: formData.partner_role.includes('destination_partner') ? 'rgba(212, 175, 55, 0.1)' : 'rgba(255, 255, 255, 0.02)' }}>
                  <input
                    type="checkbox"
                    checked={formData.partner_role.includes('destination_partner')}
                    onChange={() => handleMultiSelect('partner_role', 'destination_partner')}
                    style={checkboxStyle}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#F7F3E8' }}>Destination Partner</div>
                    <div style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)' }}>You promote destinations, host FAM trips, or support travel trade (tourism board, DMO, CVB)</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'start', padding: '16px', border: '2px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', cursor: 'pointer', background: formData.partner_role.includes('other') ? 'rgba(212, 175, 55, 0.1)' : 'rgba(255, 255, 255, 0.02)' }}>
                  <input
                    type="checkbox"
                    checked={formData.partner_role.includes('other')}
                    onChange={() => handleMultiSelect('partner_role', 'other')}
                    style={checkboxStyle}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#F7F3E8' }}>Other</div>
                    <div style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)' }}>Your organization doesn't fit the above categories</div>
                  </div>
                </label>
              </div>

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={formData.partner_role.length === 0}
                  style={{
                    padding: '12px 32px',
                    background: formData.partner_role.length > 0 ? '#D4AF37' : 'rgba(255, 255, 255, 0.1)',
                    color: formData.partner_role.length > 0 ? '#0B1120' : 'rgba(247, 243, 232, 0.4)',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: formData.partner_role.length > 0 ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Next <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Business Type & Organization Info */}
          {step === 2 && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '40px', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#F7F3E8' }}>
                Step 2: Organization Information
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>Company / Organization Name *</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>What type of organization are you? *</label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                  >
                    <option value="">Select organization type</option>
                    {Object.entries(BUSINESS_TYPE_GROUPS).map(([groupName, types]) => (
                      <optgroup key={groupName} label={groupName}>
                        {types.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>Country *</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.yourcompany.com"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Company Address</label>
                  <input
                    type="text"
                    name="company_address"
                    value={formData.company_address}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>Contact Person *</label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Job Title *</label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>Business Email *</label>
                    <input
                      type="email"
                      name="business_email"
                      value={formData.business_email}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>WhatsApp</label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>

                {isDestinationPartner && (
                  <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#F7F3E8' }}>
                      <input
                        type="checkbox"
                        name="is_government_entity"
                        checked={formData.is_government_entity}
                        onChange={handleChange}
                        style={checkboxStyle}
                      />
                      <span>This is a government tourism entity or official tourism authority</span>
                    </label>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={prevStep}
                  style={{
                    padding: '12px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#F7F3E8',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ChevronLeft size={20} /> Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!formData.company_name || !formData.business_type || !formData.country}
                  style={{
                    padding: '12px 32px',
                    background: formData.company_name && formData.business_type && formData.country ? '#D4AF37' : 'rgba(255, 255, 255, 0.1)',
                    color: formData.company_name && formData.business_type && formData.country ? '#0B1120' : 'rgba(247, 243, 232, 0.4)',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: formData.company_name && formData.business_type && formData.country ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Next <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Business Profile */}
          {step === 3 && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '40px', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#F7F3E8' }}>
                Step 3: Business Profile
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {isSupplierOrDistributor && (
                  <>
                    <div>
                      <label style={labelStyle}>Target Markets *</label>
                      <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '12px' }}>Select all markets you serve</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {TARGET_MARKETS.map(market => (
                          <label key={market.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#F7F3E8' }}>
                            <input
                              type="checkbox"
                              checked={formData.target_markets.includes(market.value)}
                              onChange={() => handleMultiSelect('target_markets', market.value)}
                            />
                            {market.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Services Offered</label>
                      <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '12px' }}>Select all that apply</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {SERVICES_OFFERED.map(service => (
                          <label key={service.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#F7F3E8' }}>
                            <input
                              type="checkbox"
                              checked={formData.services_offered.includes(service.value)}
                              onChange={() => handleMultiSelect('services_offered', service.value)}
                            />
                            {service.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Destination Coverage</label>
                      <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '8px' }}>Which destinations do your products/services cover? (e.g., Bali, Java, Thailand, Worldwide)</p>
                      <textarea
                        name="destination_coverage"
                        value={formData.destination_coverage}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Enter destinations separated by commas"
                        style={{ ...inputStyle, fontFamily: 'inherit' }}
                      />
                    </div>
                  </>
                )}

                {isDestinationPartner && (
                  <>
                    <div>
                      <label style={labelStyle}>Represented Destinations *</label>
                      <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '8px' }}>Which destinations does your organization represent or promote? (e.g., Bali, Indonesia, Southeast Asia)</p>
                      <textarea
                        name="represented_destinations"
                        value={formData.represented_destinations}
                        onChange={handleChange}
                        rows="3"
                        required={isDestinationPartner}
                        placeholder="Enter destinations separated by commas"
                        style={{ ...inputStyle, fontFamily: 'inherit' }}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Destination Capabilities *</label>
                      <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '12px' }}>Select all that apply</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {DESTINATION_CAPABILITIES.map(capability => (
                          <label key={capability.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#F7F3E8' }}>
                            <input
                              type="checkbox"
                              checked={formData.destination_capabilities.includes(capability.value)}
                              onChange={() => handleMultiSelect('destination_capabilities', capability.value)}
                            />
                            {capability.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label style={labelStyle}>Organization Description</label>
                  <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '8px' }}>Tell us about your organization and what you do</p>
                  <textarea
                    name="organization_description"
                    value={formData.organization_description}
                    onChange={handleChange}
                    rows="4"
                    style={{ ...inputStyle, fontFamily: 'inherit' }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={prevStep}
                  style={{
                    padding: '12px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#F7F3E8',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ChevronLeft size={20} /> Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  style={{
                    padding: '12px 32px',
                    background: '#D4AF37',
                    color: '#0B1120',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Next <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Documents & Submit */}
          {step === 4 && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '40px', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#F7F3E8' }}>
                Step 4: Documents & Submission
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <label style={labelStyle}>Business License / Registration</label>
                  <p style={{ fontSize: '13px', color: 'rgba(247, 243, 232, 0.7)', marginBottom: '8px' }}>Upload your business registration, tourism license, or relevant authorization document</p>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    style={inputStyle}
                  />
                  <p style={{ fontSize: '12px', color: 'rgba(247, 243, 232, 0.6)', marginTop: '4px' }}>
                    Accepted formats: PDF, JPG, PNG (max 5MB per file)
                  </p>
                </div>

                {documents.length > 0 && (
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#F7F3E8' }}>Uploaded Documents:</p>
                    {documents.map((doc, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(212, 175, 55, 0.2)', borderRadius: '4px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#F7F3E8' }}>{doc.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDocument(index)}
                          style={{ color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label style={labelStyle}>License Information (Optional)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <input
                      type="text"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleChange}
                      placeholder="License Number"
                      style={inputStyle}
                    />
                    <input
                      type="text"
                      name="issuing_authority"
                      value={formData.issuing_authority}
                      onChange={handleChange}
                      placeholder="Issuing Authority"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={prevStep}
                  style={{
                    padding: '12px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#F7F3E8',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ChevronLeft size={20} /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading || documents.length === 0}
                  style={{
                    padding: '12px 32px',
                    background: documents.length > 0 && !loading ? '#28a745' : 'rgba(255, 255, 255, 0.1)',
                    color: documents.length > 0 && !loading ? '#fff' : 'rgba(247, 243, 232, 0.4)',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: documents.length > 0 && !loading ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
      <Footer />
    </div>
  );
};
